import { API_URL } from '../config/constants';
import { publishNetWorthTrendDataChange } from '../utils/trendDataInvalidation';
import { createMutationContractError } from '../utils/mutationContracts';
import { normalizeNetWorthSummaryFreshness } from '../utils/netWorthFreshness';
import { recordNetWorthBreadcrumb } from '../utils/instrumentation';
import { adjustManualItem, applyAccountInclusion } from '../utils/netWorthOptimism';

const stateCache = new Map();
const stateRequests = new Map();
const summaryRequests = new Map();
const acknowledgedEdits = new Map();
const scopeGenerations = new Map();
let cacheGeneration = 0;
const generationFor = (scope) => `${cacheGeneration}:${scopeGenerations.get(String(scope)) || 0}`;
const VALID_SETUP_STATUSES = new Set(['not_started', 'in_progress', 'completed']);
const cacheKey = ({ householdId, cacheScope }) => (
  cacheScope == null ? null : `${String(cacheScope)}:${String(householdId)}`
);
const requestKey = ({ householdId, cacheScope }) => (
  cacheKey({ householdId, cacheScope }) || `uncached:${String(householdId)}`
);
const reconcileAcknowledgedEdits = (key, summary) => {
  if (!summary) return summary;
  const edits = acknowledgedEdits.get(key);
  if (!edits) return summary;
  let next = summary;
  for (const [id, edit] of edits.accounts) {
    const row = next.accounts.find((account) => String(account.accountId) === id);
    if (row && Number(row.version || 0) < edit.version) {
      next = applyAccountInclusion(next, id, edit.ignored, edit.version);
    }
  }
  for (const id of edits.removed) {
    const item = next.manualItems.find((row) => String(row.id) === id);
    if (item) next = adjustManualItem(next, item, true);
  }
  return next;
};
const recordAcknowledgedEdit = (key, apply) => {
  if (!key) return;
  const edits = acknowledgedEdits.get(key) || { accounts: new Map(), removed: new Set() };
  apply(edits);
  acknowledgedEdits.set(key, edits);
  const cached = stateCache.get(key);
  if (cached) stateCache.set(key, { ...cached, summary: reconcileAcknowledgedEdits(key, cached.summary) });
};
const recordFreshness = (summary) => {
  const freshness = summary?.balanceFreshness || {};
  recordNetWorthBreadcrumb('freshness', {
    initialSync: freshness.initialSync,
    unknown: freshness.unknown,
    fresh: freshness.fresh,
    recent: freshness.recent,
    refreshing: freshness.refreshing,
    stale: freshness.stale,
    providerError: freshness.providerError,
  });
};

const isObjectRecord = (value) => Boolean(
  value && typeof value === 'object' && !Array.isArray(value)
);
const CANONICAL_NUMBER_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const NET_WORTH_BREAKDOWN_KEYS = ['cash', 'investments', 'property', 'otherAssets', 'debt'];
const isFiniteNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value);
  return typeof value === 'string'
    && CANONICAL_NUMBER_PATTERN.test(value)
    && Number.isFinite(Number(value));
};
const isStrictIsoDate = (value) => {
  if (typeof value !== 'string') return false;
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};
const hasStableIdentity = (value) => (
  (typeof value === 'string' && value.trim() === value && value.length > 0)
  || (typeof value === 'number' && Number.isSafeInteger(value))
);
const validateCanonicalBreakdown = (breakdown) => (
  isObjectRecord(breakdown)
  && NET_WORTH_BREAKDOWN_KEYS.every((key) => (
    Object.hasOwn(breakdown, key) && isFiniteNumericValue(breakdown[key])
  ))
);
const getAccountIdentity = (account) => account?.accountId ?? account?.id;
const getAccountFinancialValue = (account) => (
  account?.netWorthAmount ?? account?.currentBalance ?? account?.balance
);

const createInvalidResponseError = (fallbackMessage) => {
  const error = new Error(fallbackMessage || 'The server returned an incomplete response');
  error.code = 'INVALID_RESPONSE';
  error.retryable = true;
  return error;
};

const parseJson = async (response) => {
  try {
    return { data: await response.json(), parseError: null };
  } catch (parseError) {
    return { data: null, parseError };
  }
};
const hasExpectedVersion = (value) => value !== null
  && value !== undefined
  && Number.isInteger(Number(value));

const ensureOk = async (response, fallbackMessage) => {
  const { data, parseError } = await parseJson(response);
  if (!response.ok) {
    throw createMutationContractError({
      response,
      data: isObjectRecord(data) ? data : {},
      fallbackMessage,
    });
  }
  if (parseError) throw createInvalidResponseError(fallbackMessage);
  return data;
};

const validateNetWorthSummary = (summary, fallbackMessage) => {
  const latestSnapshotValid = summary?.latestSnapshot === null || (
    isObjectRecord(summary?.latestSnapshot)
    && isStrictIsoDate(summary.latestSnapshot.snapshotDate)
    && isFiniteNumericValue(summary.latestSnapshot.netWorth)
  );
  if (
    !isObjectRecord(summary)
    || !Array.isArray(summary.accounts)
    || !Array.isArray(summary.institutions)
    || !Array.isArray(summary.manualItems)
    || !Object.hasOwn(summary, 'latestSnapshot')
    || !latestSnapshotValid
    || typeof summary.balancesStale !== 'boolean'
    || !isFiniteNumericValue(summary.totalAssets)
    || !isFiniteNumericValue(summary.totalLiabilities)
    || !isFiniteNumericValue(summary.netWorth)
    || !validateCanonicalBreakdown(summary.breakdown)
    || summary.accounts.some((account) => (
      !isObjectRecord(account)
      || !hasStableIdentity(getAccountIdentity(account))
      || !isFiniteNumericValue(getAccountFinancialValue(account))
    ))
    || summary.manualItems.some((item) => (
      !isObjectRecord(item)
      || !hasStableIdentity(item.id)
      || !isFiniteNumericValue(item.estimatedValue)
    ))
  ) {
    throw createInvalidResponseError(fallbackMessage);
  }
  return summary;
};

const validateNetWorthState = (data, fallbackMessage) => {
  if (!isObjectRecord(data) || !isObjectRecord(data.setupStatus)) {
    throw createInvalidResponseError(fallbackMessage);
  }

  const status = String(data.setupStatus.status || '').trim();
  const rawCurrentStep = data.setupStatus.currentStep ?? data.setupStatus.current_step;
  if (
    !VALID_SETUP_STATUSES.has(status)
    || !Object.hasOwn(data, 'summary')
    || !Number.isInteger(rawCurrentStep)
    || rawCurrentStep < 0
  ) {
    throw createInvalidResponseError(fallbackMessage);
  }

  if (status === 'completed') {
    validateNetWorthSummary(data.summary, fallbackMessage);
  } else if (data.summary !== null) {
    throw createInvalidResponseError(fallbackMessage);
  }

  return {
    ...data,
    setupStatus: {
      ...data.setupStatus,
      status,
      currentStep: rawCurrentStep,
    },
  };
};

const validateNetWorthSnapshot = (snapshot, fallbackMessage) => {
  if (
    !isObjectRecord(snapshot)
    || !isStrictIsoDate(snapshot.snapshotDate)
    || !isFiniteNumericValue(snapshot.totalAssets)
    || !isFiniteNumericValue(snapshot.totalLiabilities)
    || !isFiniteNumericValue(snapshot.netWorth)
    || !validateCanonicalBreakdown(snapshot.breakdown)
    || !Array.isArray(snapshot.accountBalances)
  ) {
    throw createInvalidResponseError(fallbackMessage);
  }

  const accountIds = new Set();
  if (snapshot.accountBalances.some((account) => {
    const accountId = account?.accountId;
    if (
      !isObjectRecord(account)
      || !hasStableIdentity(accountId)
      || !isFiniteNumericValue(account.value)
      || accountIds.has(String(accountId))
    ) return true;
    accountIds.add(String(accountId));
    return false;
  })) {
    throw createInvalidResponseError(fallbackMessage);
  }

  return {
    ...snapshot,
    totalAssets: Number(snapshot.totalAssets),
    totalLiabilities: Number(snapshot.totalLiabilities),
    netWorth: Number(snapshot.netWorth),
    breakdown: Object.fromEntries(NET_WORTH_BREAKDOWN_KEYS.map((key) => (
      [key, Number(snapshot.breakdown[key])]
    ))),
    accountBalances: snapshot.accountBalances.map((account) => ({
      ...account,
      value: Number(account.value),
    })),
  };
};

const validateNetWorthSnapshots = (data, fallbackMessage) => {
  if (!isObjectRecord(data) || !Array.isArray(data.snapshots)) {
    throw createInvalidResponseError(fallbackMessage);
  }
  return data.snapshots.map((snapshot) => validateNetWorthSnapshot(snapshot, fallbackMessage));
};

export const fetchNetWorthState = async ({ authenticatedFetch, householdId, cacheScope = null }) => {
  const generation = generationFor(cacheScope);
  const key = requestKey({ householdId, cacheScope });
  if (stateRequests.has(key)) {
    recordNetWorthBreadcrumb('state_request', { deduplicated: true });
    return stateRequests.get(key);
  }
  recordNetWorthBreadcrumb('state_request', {
    cache: cacheKey({ householdId, cacheScope })
      ? (stateCache.has(key) ? 'hit' : 'miss')
      : 'disabled',
    deduplicated: false,
  });
  const request = (async () => {
    const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/state`);
    const data = validateNetWorthState(
      await ensureOk(response, 'Failed to load net worth'),
      'Failed to load net worth'
    );
    const state = {
      setupStatus: {
        status: data.setupStatus.status,
        currentStep: Number(data?.setupStatus?.currentStep ?? data?.setupStatus?.current_step ?? 0) || 0,
        completedAt: data?.setupStatus?.completedAt || data?.setupStatus?.completed_at || null,
      },
      summary: reconcileAcknowledgedEdits(key, normalizeNetWorthSummaryFreshness(data?.summary)),
    };
    if (cacheKey({ householdId, cacheScope }) && generation === generationFor(cacheScope)) stateCache.set(key, state);
    recordFreshness(state.summary);
    return state;
  })().finally(() => { if (stateRequests.get(key) === request) stateRequests.delete(key); });
  stateRequests.set(key, request);
  return request;
};

export const getCachedNetWorthState = (householdId, cacheScope = null) => (
  householdId == null || cacheScope == null
    ? null
    : stateCache.get(cacheKey({ householdId, cacheScope })) || null
);

export const fetchNetWorthSetupStatus = async ({ authenticatedFetch, householdId }) => {
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/setup-status`);
  const data = await ensureOk(response, 'Failed to load net worth setup');
  return {
    status: data?.status || 'not_started',
    currentStep: Number(data?.currentStep ?? data?.current_step ?? 0) || 0,
    completedAt: data?.completedAt || data?.completed_at || null,
  };
};

export const saveNetWorthSetupStatus = async ({ authenticatedFetch, householdId, status, currentStep }) => {
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/setup-status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, currentStep }),
  });
  const data = await ensureOk(response, 'Failed to save net worth setup');
  return {
    status: data?.status || status,
    currentStep: Number(data?.currentStep ?? data?.current_step ?? currentStep) || 0,
    completedAt: data?.completedAt || data?.completed_at || null,
  };
};

export const fetchNetWorthSummary = async ({ authenticatedFetch, householdId, cacheScope = null }) => {
  const generation = generationFor(cacheScope);
  const key = requestKey({ householdId, cacheScope });
  if (summaryRequests.has(key)) return summaryRequests.get(key);
  const request = (async () => {
    const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/summary`);
    const data = reconcileAcknowledgedEdits(key, normalizeNetWorthSummaryFreshness(validateNetWorthSummary(
      await ensureOk(response, 'Failed to fetch net worth summary'),
      'Failed to fetch net worth summary'
    )));
    const current = cacheKey({ householdId, cacheScope }) ? stateCache.get(key) : null;
    if (current && generation === generationFor(cacheScope)) stateCache.set(key, { ...current, summary: data });
    recordFreshness(data);
    return data;
  })().finally(() => { if (summaryRequests.get(key) === request) summaryRequests.delete(key); });
  summaryRequests.set(key, request);
  return request;
};

export const resetNetWorthServiceCache = () => {
  cacheGeneration += 1;
  acknowledgedEdits.clear();
  scopeGenerations.clear();
  stateCache.clear();
  stateRequests.clear();
  summaryRequests.clear();
};

export const clearNetWorthCacheScope = (cacheScope) => {
  if (cacheScope == null) return;
  const prefix = `${String(cacheScope)}:`;
  scopeGenerations.set(String(cacheScope), (scopeGenerations.get(String(cacheScope)) || 0) + 1);
  [...acknowledgedEdits.keys()].forEach((key) => { if (key.startsWith(prefix)) acknowledgedEdits.delete(key); });
  for (const requests of [stateRequests, summaryRequests]) {
    [...requests.keys()].forEach((key) => { if (key.startsWith(prefix)) requests.delete(key); });
  }
  [...stateCache.keys()].forEach((key) => {
    if (key.startsWith(prefix)) stateCache.delete(key);
  });
};

export const fetchNetWorthAccounts = async ({ authenticatedFetch, householdId }) => {
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/accounts/classified`);
  return ensureOk(response, 'Failed to fetch net worth accounts');
};

export const fetchNetWorthItems = async ({ authenticatedFetch, householdId, itemType = null }) => {
  const query = itemType ? `?item_type=${encodeURIComponent(itemType)}` : '';
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/items${query}`);
  const data = await ensureOk(response, 'Failed to fetch net worth items');
  return Array.isArray(data?.items) ? data.items : [];
};

export const createNetWorthItem = async ({ authenticatedFetch, householdId, payload, idempotencyKey = null }) => {
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/items`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    body: JSON.stringify(payload),
  });
  const data = await ensureOk(response, 'Failed to create net worth item');
  publishNetWorthTrendDataChange({ householdId });
  return data?.item || null;
};

export const updateNetWorthItem = async ({
  authenticatedFetch,
  householdId,
  itemId,
  payload,
  expectedVersion = null,
}) => {
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/items/${itemId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      ...(hasExpectedVersion(expectedVersion) ? { expectedVersion: Number(expectedVersion) } : {}),
    }),
  });
  const data = await ensureOk(response, 'Failed to update net worth item');
  publishNetWorthTrendDataChange({ householdId });
  return data?.item || null;
};

export const deleteNetWorthItem = async ({ authenticatedFetch, householdId, itemId, cacheScope = null }) => {
  const generation = generationFor(cacheScope);
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/items/${itemId}`, {
    method: 'DELETE',
  });
  const data = await ensureOk(response, 'Failed to delete net worth item');
  if (generation === generationFor(cacheScope)) recordAcknowledgedEdit(cacheKey({ householdId, cacheScope }), (edits) => edits.removed.add(String(itemId)));
  publishNetWorthTrendDataChange({ householdId });
  return data;
};

export const toggleNetWorthAccountIgnore = async ({
  authenticatedFetch,
  householdId,
  accountId,
  ignored,
  expectedVersion = null,
  cacheScope = null,
}) => {
  const generation = generationFor(cacheScope);
  const response = await authenticatedFetch(
    `${API_URL}/net-worth/households/${householdId}/accounts/${encodeURIComponent(accountId)}/ignore`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ignored,
        ...(hasExpectedVersion(expectedVersion) ? { expectedVersion: Number(expectedVersion) } : {}),
      }),
    }
  );
  const data = await ensureOk(response, 'Failed to update account');
  const account = data?.account || data;
  const version = Number(account?.version);
  if (cacheScope != null && (!Number.isInteger(version) || version <= Number(expectedVersion ?? -1)
    || String(account?.accountId ?? account?.id) !== String(accountId) || account?.isIgnored !== ignored)) {
    throw createInvalidResponseError('Could not confirm this account change');
  }
  if (generation === generationFor(cacheScope)) recordAcknowledgedEdit(cacheKey({ householdId, cacheScope }), (edits) => {
    const previous = edits.accounts.get(String(accountId));
    if (!previous || previous.version < version) edits.accounts.set(String(accountId), { ignored, version });
  });
  publishNetWorthTrendDataChange({ householdId });
  return data;
};

export const refreshNetWorthBalances = async ({ authenticatedFetch, householdId, trigger = 'manual' }) => {
  recordNetWorthBreadcrumb('refresh_triggered', { trigger });
  const response = await authenticatedFetch(`${API_URL}/net-worth/households/${householdId}/refresh-balances`, {
    method: 'POST',
  });
  const data = await ensureOk(response, 'Failed to refresh balances');
  publishNetWorthTrendDataChange({ householdId });
  return data;
};

export const fetchNetWorthSnapshots = async ({ authenticatedFetch, householdId, limit = 90 }) => {
  const response = await authenticatedFetch(
    `${API_URL}/net-worth/households/${householdId}/snapshots?limit=${encodeURIComponent(limit)}`
  );
  const data = await ensureOk(response, 'Failed to fetch net worth snapshots');
  return validateNetWorthSnapshots(data, 'Failed to fetch net worth snapshots');
};
